import * as React from "react";
import { ReleaseRevision, ReleaseRevisionResource } from "../types/release";
import { linksOf, request } from "../../../services/cpln";
import { convertAllLinksToRelative, parseLink } from "../../../utils/linkParser/linkParser";
import { clearItem, k8sKeySort, toSortedJSON } from "../../../services/utils";
import jsYaml from "js-yaml";
import ReactDiff from "react-diff-viewer";
import { Button, Checkbox, Modal, notification } from "antd";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setUseSplitViewComparison } from "../../../store/uiDataSlice";
import { AlertTriangle, CheckCircle, Slash, HelpCircle, Lock } from "react-feather";
import { isEqual } from "lodash";
import { InfoTooltip } from "../../../components/InfoTooltip";

// Rules for fields where the API returns a default value but templates omit them.
// Add new entries here as you discover more false-positive drift cases.
const DEFAULT_RULES: { path: (string | "*")[]; defaultValue: any }[] = [
  { path: ["spec", "supportDynamicTags"], defaultValue: false },
  { path: ["spec", "env"], defaultValue: [] },
  { path: ["spec", "containers", "*", "env"], defaultValue: [] },
  { path: ["spec", "firewallConfig", "internal", "inboundAllowWorkload"], defaultValue: [] },
  { path: ["spec", "firewallConfig", "external", "inboundBlockedCIDR"], defaultValue: [] },
  { path: ["spec", "firewallConfig", "external", "outboundAllowHostname"], defaultValue: [] },
  { path: ["spec", "firewallConfig", "external", "outboundAllowPort"], defaultValue: [] },
  { path: ["spec", "firewallConfig", "external", "outboundBlockedCIDR"], defaultValue: [] },
  { path: ["spec", "loadBalancer", "direct", "enabled"], defaultValue: false },
  { path: ["spec", "loadBalancer", "direct", "ports"], defaultValue: [] },
  { path: ["spec", "loadBalancer", "multiZone", "enabled"], defaultValue: false },
  { path: ["spec", "defaultOptions", "debug"], defaultValue: false },
  { path: ["spec", "defaultOptions", "suspend"], defaultValue: false },
  { path: ["spec", "defaultOptions", "multiZone", "enabled"], defaultValue: false },
  { path: ["spec", "containers", "*", "volumes", "*", "recoveryPolicy"], defaultValue: "retain" },
  { path: ["spec", "containers", "*", "inheritEnv"], defaultValue: false },
  { path: ["spec", "firewallConfig", "external", "inboundAllowCIDR"], defaultValue: [] },
  { path: ["spec", "firewallConfig", "external", "outboundAllowCIDR"], defaultValue: [] },
  { path: ["spec", "loadBalancer", "replicaDirect"], defaultValue: false },
];

// Rules for arrays where element order is not semantically meaningful.
// "*" in the path means "iterate all elements of the array at this level".
const SORT_RULES: { path: (string | "*")[]; sortKey: string }[] = [
  { path: ["spec", "env"], sortKey: "name" },
  { path: ["spec", "containers", "*", "env"], sortKey: "name" },
  { path: ["targetLinks"], sortKey: "" },
];

function getNestedValue(obj: any, path: string[]): { exists: boolean; value: any } {
  let current = obj;
  for (const key of path) {
    if (current == null || typeof current !== "object" || !(key in current)) {
      return { exists: false, value: undefined };
    }
    current = current[key];
  }
  return { exists: true, value: current };
}

function deleteNestedField(obj: any, path: string[]): void {
  let current = obj;
  for (let i = 0; i < path.length - 1; i++) {
    if (current == null || typeof current !== "object") return;
    current = current[path[i]];
  }
  if (current != null && typeof current === "object") {
    delete current[path[path.length - 1]];
  }
}

/**
 * Resolve a wildcard path pattern into concrete paths by expanding "*"
 * into actual array indices. e.g. ["spec","containers","*","env"] →
 * [["spec","containers","0","env"], ["spec","containers","1","env"]]
 */
function resolvePaths(obj: any, pattern: (string | "*")[]): string[][] {
  let results: string[][] = [[]];
  let currents: any[] = [obj];

  for (const segment of pattern) {
    const nextResults: string[][] = [];
    const nextCurrents: any[] = [];

    for (let i = 0; i < currents.length; i++) {
      const current = currents[i];
      if (current == null || typeof current !== "object") continue;

      if (segment === "*") {
        if (Array.isArray(current)) {
          for (let idx = 0; idx < current.length; idx++) {
            nextResults.push([...results[i], String(idx)]);
            nextCurrents.push(current[idx]);
          }
        }
      } else {
        if (segment in current) {
          nextResults.push([...results[i], segment]);
          nextCurrents.push(current[segment]);
        }
      }
    }

    results = nextResults;
    currents = nextCurrents;
  }

  return results;
}

/**
 * For each default rule, if both sides are "effectively default"
 * (missing or equal to the default value), strip the field from both.
 * If one side has a non-default value, keep both so the real diff shows.
 * Supports "*" wildcards in paths (e.g. spec.containers.*.env).
 */
function normalizeDefaults(templateObj: any, liveObj: any): void {
  for (const rule of DEFAULT_RULES) {
    const hasWildcard = rule.path.includes("*");

    if (!hasWildcard) {
      const t = getNestedValue(templateObj, rule.path as string[]);
      const l = getNestedValue(liveObj, rule.path as string[]);
      const tIsDefault = !t.exists || isEqual(t.value, rule.defaultValue);
      const lIsDefault = !l.exists || isEqual(l.value, rule.defaultValue);
      if (tIsDefault && lIsDefault) {
        deleteNestedField(templateObj, rule.path as string[]);
        deleteNestedField(liveObj, rule.path as string[]);
      }
    } else {
      const tPaths = resolvePaths(templateObj, rule.path);
      const lPaths = resolvePaths(liveObj, rule.path);
      const seen = new Set<string>();
      const allPaths: string[][] = [];
      for (const p of [...tPaths, ...lPaths]) {
        const key = p.join("\0");
        if (!seen.has(key)) {
          seen.add(key);
          allPaths.push(p);
        }
      }

      for (const concretePath of allPaths) {
        const t = getNestedValue(templateObj, concretePath);
        const l = getNestedValue(liveObj, concretePath);
        const tIsDefault = !t.exists || isEqual(t.value, rule.defaultValue);
        const lIsDefault = !l.exists || isEqual(l.value, rule.defaultValue);
        if (tIsDefault && lIsDefault) {
          deleteNestedField(templateObj, concretePath);
          deleteNestedField(liveObj, concretePath);
        }
      }
    }
  }
}

/**
 * Recursively remove empty plain objects left behind after stripping defaults.
 * e.g. if direct.enabled and direct.ports are both stripped, direct becomes {} → pruned.
 */
function pruneEmptyObjects(obj: any): void {
  if (obj == null || typeof obj !== "object" || Array.isArray(obj)) return;
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    if (val != null && typeof val === "object" && !Array.isArray(val)) {
      pruneEmptyObjects(val);
      if (Object.keys(val).length === 0) {
        delete obj[key];
      }
    }
  }
}

/**
 * Sort arrays at configured paths so that element order differences
 * don't produce false drift. Supports "*" wildcard for array indices.
 */
function normalizeArrayOrder(obj: any): void {
  for (const rule of SORT_RULES) {
    const lastKey = rule.path[rule.path.length - 1];
    const parentPath = rule.path.slice(0, -1);

    // Resolve parents, expanding "*" wildcards
    let parents: any[] = [obj];
    for (const key of parentPath) {
      const next: any[] = [];
      for (const p of parents) {
        if (p == null || typeof p !== "object") continue;
        if (key === "*") {
          if (Array.isArray(p)) {
            next.push(...p);
          }
        } else if (key in p) {
          next.push(p[key]);
        }
      }
      parents = next;
    }

    for (const parent of parents) {
      if (parent == null || typeof parent !== "object") continue;
      const arr = parent[lastKey];
      if (Array.isArray(arr)) {
        arr.sort((a: any, b: any) => {
          if (!rule.sortKey) return String(a ?? "").localeCompare(String(b ?? ""));
          const aKey = a?.[rule.sortKey] ?? "";
          const bKey = b?.[rule.sortKey] ?? "";
          return String(aKey).localeCompare(String(bKey));
        });
      }
    }
  }
}

/**
 * Convert //gvc/GVCNAME/kind/name → //kind/name for links within the same GVC.
 * Templates use the short form; the API returns absolute links that convertAllLinksToRelative
 * turns into //gvc/X/kind/name. This second pass normalizes to the shortest form.
 */
function normalizeGvcLinks(obj: any, gvc: string): void {
  if (obj == null || typeof obj !== "object") return;
  const prefix = `//gvc/${gvc}/`;
  for (const key of Object.keys(obj)) {
    if (key === "links") continue;
    const val = obj[key];
    if (typeof val === "string" && val.startsWith(prefix)) {
      obj[key] = "//" + val.slice(prefix.length);
    } else if (typeof val === "object") {
      normalizeGvcLinks(val, gvc);
    }
  }
}

interface DriftItem {
  link: string;
  kind: string;
  name: string;
  templateYaml: string;
  liveYaml: string;
  hasDrift: boolean;
  notFound: boolean;
  templateMissing: boolean;
  isSecret: boolean;
}

function resourceToYaml(data: any, gvc?: string): string {
  if (!data) return "";
  const obj = JSON.parse(JSON.stringify(data));

  // Always strip fields that create false drift
  delete obj.description;
  delete obj.gvc;
  if (obj.tags) {
    delete obj.tags["cpln/deployTimestamp"];
    delete obj.tags["cpln/release"];
  }

  // Normalize links so relative vs absolute don't create false diffs
  convertAllLinksToRelative(obj);
  if (gvc) {
    normalizeGvcLinks(obj, gvc);
  }

  // Always use slim to strip system fields
  clearItem(obj);

  const sorted = toSortedJSON(obj);
  return jsYaml.dump(sorted, { indent: 2, noRefs: true, sortKeys: k8sKeySort });
}

function getLatestRevision(revisions: ReleaseRevision[]): ReleaseRevision | null {
  if (revisions.length === 0) return null;
  return revisions.toSorted((a, b) => b.version - a.version)[0];
}

export function computeDriftItems(
  revisions: ReleaseRevision[],
  items: any[],
  itemsNotFound: any[],
  revealedSecrets?: Record<string, any>,
): DriftItem[] {
  const revision = getLatestRevision(revisions);
  if (!revision) return [];

  const driftItems: DriftItem[] = revision.info.resources.map((resource: ReleaseRevisionResource) => {
    const liveItem = items.find((item) => linksOf(item).self === resource.link);
    const isNotFound = itemsNotFound.some((item) => item.link === resource.link);
    const parsedLink = parseLink(resource.link, { useInputCtx: true });
    const name = parsedLink.name;
    const resourceGvc = parsedLink.gvc;
    const templateMissing = resource.template == null;
    const isSecret = resource.kind === "secret";

    // Secrets: skip comparison unless revealed
    if (isSecret) {
      const revealedLive = revealedSecrets?.[resource.link];

      if (!revealedLive) {
        return {
          link: resource.link,
          kind: resource.kind,
          name,
          templateYaml: "",
          liveYaml: "",
          hasDrift: isNotFound,
          notFound: isNotFound,
          templateMissing: false,
          isSecret: true,
        };
      }

      // Revealed — compare like any other resource
      let templateYaml: string;
      let liveYaml: string;

      if (!templateMissing) {
        const tClone = JSON.parse(JSON.stringify(resource.template));
        const lClone = JSON.parse(JSON.stringify(revealedLive));
        normalizeDefaults(tClone, lClone);
        pruneEmptyObjects(tClone);
        pruneEmptyObjects(lClone);
        normalizeArrayOrder(tClone);
        normalizeArrayOrder(lClone);
        templateYaml = resourceToYaml(tClone, resourceGvc);
        liveYaml = resourceToYaml(lClone, resourceGvc);
      } else {
        templateYaml = resourceToYaml(resource.template, resourceGvc);
        liveYaml = resourceToYaml(revealedLive, resourceGvc);
      }

      return {
        link: resource.link,
        kind: resource.kind,
        name,
        templateYaml,
        liveYaml,
        hasDrift: isNotFound || (!templateMissing && templateYaml !== liveYaml),
        notFound: isNotFound,
        templateMissing,
        isSecret: true,
      };
    }

    let templateYaml: string;
    let liveYaml: string;

    if (!templateMissing && liveItem) {
      // Both exist — normalize before serializing
      const tClone = JSON.parse(JSON.stringify(resource.template));
      const lClone = JSON.parse(JSON.stringify(liveItem));
      normalizeDefaults(tClone, lClone);
      pruneEmptyObjects(tClone);
      pruneEmptyObjects(lClone);
      normalizeArrayOrder(tClone);
      normalizeArrayOrder(lClone);
      templateYaml = resourceToYaml(tClone, resourceGvc);
      liveYaml = resourceToYaml(lClone, resourceGvc);
    } else {
      templateYaml = resourceToYaml(resource.template, resourceGvc);
      liveYaml = liveItem ? resourceToYaml(liveItem, resourceGvc) : "";
    }

    let hasDrift: boolean;
    if (templateMissing) {
      hasDrift = false;
    } else if (isNotFound) {
      hasDrift = true;
    } else if (!liveItem) {
      hasDrift = true;
    } else {
      hasDrift = templateYaml !== liveYaml;
    }

    return {
      link: resource.link,
      kind: resource.kind,
      name,
      templateYaml,
      liveYaml,
      hasDrift,
      notFound: isNotFound,
      templateMissing,
      isSecret: false,
    };
  });

  // Sort: drift first, then alphabetically by kind/name
  return driftItems.toSorted((a, b) => {
    if (a.hasDrift && !b.hasDrift) return -1;
    if (!a.hasDrift && b.hasDrift) return 1;
    const kindCmp = a.kind.localeCompare(b.kind);
    if (kindCmp !== 0) return kindCmp;
    return a.name.localeCompare(b.name);
  });
}

interface Props {
  revisions: ReleaseRevision[];
  items: any[];
  itemsNotFound: any[];
}

export const ReleaseDrift: React.FC<Props> = ({ revisions, items, itemsNotFound }) => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.theme.theme);
  const useSplitViewComparison = useAppSelector((state) => state.uiData.useSplitViewComparison);

  const [selectedLink, setSelectedLink] = React.useState<string | null>(null);
  const revealedSecrets = {};
  // const [revealedSecrets, setRevealedSecrets] = React.useState<Record<string, any>>({});
  // const [revealingSecrets, setRevealingSecrets] = React.useState(false);

  const driftItems = React.useMemo(() => computeDriftItems(revisions, items, itemsNotFound, revealedSecrets), [
    revisions,
    items,
    itemsNotFound,
    revealedSecrets,
  ]);

  const driftCount = driftItems.filter((d) => d.hasDrift).length;
  const selected = driftItems.find((d) => d.link === selectedLink) || null;
  // const hasSecrets = driftItems.some((d) => d.isSecret);
  // const secretsRevealed = Object.keys(revealedSecrets).length > 0;

  // Auto-select first drift item if nothing selected
  React.useEffect(() => {
    if (!selectedLink && driftItems.length > 0) {
      setSelectedLink(driftItems[0].link);
    }
  }, [driftItems]);

  // function handleRevealSecrets() {
  //   Modal.confirm({
  //     title: "Reveal Secrets",
  //     content: "Secret values will be revealed and compared for drift detection. This action will be logged in the audit trail.",
  //     okText: "Reveal",
  //     async onOk() {
  //       setRevealingSecrets(true);
  //       try {
  //         const revision = getLatestRevision(revisions);
  //         if (!revision) return;
  //         const secretResources = revision.info.resources.filter((r) => r.kind === "secret");
  //         const results = await Promise.allSettled(
  //           secretResources.map((r) => request({ url: `${r.link}/-reveal` })),
  //         );
  //         const revealed: Record<string, any> = {};
  //         results.forEach((result, i) => {
  //           if (result.status === "fulfilled") {
  //             revealed[secretResources[i].link] = result.value.data;
  //           }
  //         });
  //         setRevealedSecrets(revealed);
  //       } catch (e: any) {
  //         notification.error({ message: "Failed to reveal secrets", description: e.message });
  //       } finally {
  //         setRevealingSecrets(false);
  //       }
  //     },
  //   });
  // }

  if (revisions.length === 0) {
    return <div className="p-4 color-label">No revision data available.</div>;
  }

  return (
    <div className="flex gap-4">
      {/* Left panel - resource list */}
      <div className="flex flex-col border rounded" style={{ width: 280, minWidth: 280 }}>
        <div className="px-3 py-2 border-b" style={{ backgroundColor: "var(--color-drop)" }}>
          <div className="text-sm font-semibold">
            {driftCount > 0 ? (
              <span style={{ color: "var(--color-danger)" }}>
                {driftCount} of {driftItems.length} items have drift
              </span>
            ) : (
              <span style={{ color: "var(--color-action)" }}>All {driftItems.length} items match</span>
            )}
          </div>
        </div>
        <div>
          {driftItems.map((item) => (
            <div
              key={item.link}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer"
              style={{
                backgroundColor: selectedLink === item.link ? "var(--color-drop-secondary)" : undefined,
              }}
              onClick={() => setSelectedLink(item.link)}
            >
              <DriftIndicator item={item} revealed={!!revealedSecrets[item.link]} />
              <div className="flex flex-col min-w-0">
                <span className="text-sm truncate" style={{ color: "var(--color-default)" }}>
                  {item.name}
                </span>
                <span className="text-xs" style={{ color: "var(--color-label)" }}>
                  {item.kind}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel - diff viewer */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <InfoTooltip
              title={
                "Drift detection compares the original template output of this release (latest revision) with the current live state of each resource. " +
                "Differences indicate manual changes made outside of the template, which could be overwritten during an upgrade. " +
                "Secret contents can be revealed and compared using the Reveal Secrets button. Resources that have been deleted from the platform are flagged separately."
              }
              style={{ marginLeft: 0 }}
            />
            {selected && (
              <div className="text-base font-semibold">
                {selected.name}{" "}
                <span className="font-normal" style={{ color: "var(--color-label)" }}>
                  ({selected.kind})
                </span>
              </div>
            )}
            <Checkbox
              checked={useSplitViewComparison}
              onChange={(e) => dispatch(setUseSplitViewComparison(e.target.checked))}
            >
              Split View
            </Checkbox>
            {/* {hasSecrets && (
              <Button
                size="small"
                onClick={handleRevealSecrets}
                loading={revealingSecrets}
                disabled={secretsRevealed}
                icon={<Lock size={14} />}
              >
                {secretsRevealed ? "Secrets Revealed" : "Reveal Secrets"}
              </Button>
            )} */}
          </div>
        </div>

        {/* Diff content */}
        <div className="border rounded" style={{ minHeight: 500 }}>
          {!selected ? (
            <div className="flex items-center justify-center color-label" style={{ minHeight: 500 }}>
              Select a resource to view drift
            </div>
          ) : selected.isSecret && !selected.notFound && !revealedSecrets[selected.link] ? (
            <div
              className="flex items-center justify-center gap-2"
              style={{ color: "var(--color-label)", minHeight: 500 }}
            >
              <Lock size={18} />
              <span>Reveal secrets to compare for drift</span>
            </div>
          ) : selected.templateMissing ? (
            <div
              className="flex items-center justify-center gap-2"
              style={{ color: "var(--color-label)", minHeight: 500 }}
            >
              <HelpCircle size={18} />
              <span>Template data not available for this resource</span>
            </div>
          ) : selected.notFound ? (
            <div>
              <div
                className="flex items-center gap-2 px-3 py-2"
                style={{ backgroundColor: "rgba(255, 68, 68, 0.1)", color: "var(--color-danger)" }}
              >
                <Slash size={16} />
                <span className="text-sm font-semibold">Resource has been deleted</span>
              </div>
              <div>
                <ReactDiff
                  splitView={useSplitViewComparison}
                  oldValue={selected.templateYaml}
                  newValue=""
                  leftTitle="Template (Expected)"
                  rightTitle="Live (Deleted)"
                  useDarkTheme={theme === "dark"}
                />
              </div>
            </div>
          ) : !selected.hasDrift ? (
            <div
              className="flex items-center justify-center gap-2"
              style={{ color: "var(--color-action)", minHeight: 500 }}
            >
              <CheckCircle size={18} />
              <span>Resource matches template — no drift detected</span>
            </div>
          ) : (
            <ReactDiff
              splitView={useSplitViewComparison}
              oldValue={selected.templateYaml}
              newValue={selected.liveYaml}
              leftTitle="Template (Expected)"
              rightTitle="Live (Current)"
              useDarkTheme={theme === "dark"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const DriftIndicator: React.FC<{ item: DriftItem; revealed?: boolean }> = ({ item, revealed }) => {
  if (item.notFound) {
    return <Slash size={14} style={{ color: "var(--color-danger)", flexShrink: 0 }} />;
  }
  if (item.isSecret && !revealed) {
    return <Lock size={14} style={{ color: "var(--color-label)", flexShrink: 0 }} />;
  }
  if (item.templateMissing) {
    return <HelpCircle size={14} style={{ color: "var(--color-label)", flexShrink: 0 }} />;
  }
  if (item.hasDrift) {
    return <AlertTriangle size={14} style={{ color: "#fa8c16", flexShrink: 0 }} />;
  }
  return <CheckCircle size={14} style={{ color: "var(--color-action)", flexShrink: 0 }} />;
};
